import React from "react";
import { Box, Tooltip, Typography } from "@mui/material";
import { GridAlignment, GridColDef } from "@mui/x-data-grid";
import StatusCell from "./statusIcon";
import { useClassificationContext } from "../../contexts/ClassificationContext";
import useFetchReasonsByClassification from "../../hooks/useFetchReasonsByClassification";

interface ColumnProps {
  field?: string;
  headerName?: string;
  minWidth?: string;
  align?: GridAlignment;
  headerAlign?: GridAlignment;
}

/** Context-aware cell: looks up classification name from global context */
const ClassificationCell: React.FC<{ classificationId: number | null }> = ({
  classificationId,
}) => {
  const { allClassifications } = useClassificationContext();
  const classification = allClassifications.find(
    (c: any) => Number(c.id) === Number(classificationId)
  );
  const name = classification?.issue_classification_name || "";
  return (
    <Tooltip title={name} placement="top" arrow>
      <Box className="word-break-all">{name}</Box>
    </Tooltip>
  );
};

/** Context-aware cell: fetches + caches reasons per classification, then looks up reason name */
const ReasonCell: React.FC<{
  reasonId: number | null;
  classificationId: number | null;
}> = ({ reasonId, classificationId }) => {
  const [reasons] = useFetchReasonsByClassification(classificationId);
  const reason = reasons.find((r: any) => Number(r.id) === Number(reasonId));
  const name = reason?.reason_name || "";
  return (
    <Tooltip
      title={<Typography sx={{ fontSize: "0.875rem" }}>{name}</Typography>}
      placement="top"
      arrow
    >
      <Box className="word-break-all max-width100">
        {name && name?.length > 49 ? `${name.substring(0, 50)}...` : name || ""}
      </Box>
    </Tooltip>
  );
};

export const UserColumn = ({
  field = "username",
  headerName = "User Name",
  minWidth = "140",
  align = "center",
  headerAlign = "center",
}: ColumnProps): GridColDef => {
  const column: GridColDef = {
    field: field,
    headerName: headerName,
    headerAlign: headerAlign,
    align: align,
    minWidth: parseInt(minWidth),
    renderCell: (params: any) => (
      <Tooltip title={params?.row?.assigned_user} placement="top" arrow>
        <Box className="word-break-all">{`${
          params.row?.assigned_user || ""
        }`}</Box>
      </Tooltip>
    ),
  };
  return column;
};

export const CurrentStatusColumn = ({
  field = "current_status",
  headerName = "Status",
  minWidth = "80",
  align = "center",
  headerAlign = "center",
}: ColumnProps): GridColDef => {
  const column: GridColDef = {
    field: field,
    headerName: headerName,
    headerAlign: headerAlign,
    align: align,
    minWidth: parseInt(minWidth),
    renderCell: (params: any) => (
      <StatusCell status={params?.row?.current_status} />
    ),
  };
  return column;
};

export const CommentColumn = ({
  field = "comment",
  headerName = "Comment",
  minWidth = "140",
  align = "center",
  headerAlign = "center",
}: ColumnProps): GridColDef => {
  const column: GridColDef = {
    field: field,
    headerName: headerName,
    headerAlign: headerAlign,
    align: align,
    minWidth: parseInt(minWidth),
    renderCell: (params: any) => (
      <Tooltip
        title={
          <Typography sx={{ fontSize: "0.875rem" }}>
            {params?.row?.comment || ""}
          </Typography>
        }
        placement="top"
        arrow
      >
        <Box className="word-break-all max-width100">
          {params?.row?.comment && params?.row?.comment.length > 49
            ? `${params?.row?.comment.substring(0, 50)}...`
            : params?.row?.comment || ""}
        </Box>
      </Tooltip>
    ),
  };
  return column;
};

export const ClassificationColumn = ({
  field = "issue_classification_id",
  headerName = "Classification",
  minWidth = "160",
  align = "center",
  headerAlign = "center",
}: ColumnProps): GridColDef => {
  const column: GridColDef = {
    field: field,
    headerName: headerName,
    headerAlign: headerAlign,
    align: align,
    minWidth: parseInt(minWidth),
    renderCell: (params: any) => (
      <ClassificationCell
        classificationId={params?.row?.issue_classification_id ?? null}
      />
    ),
  };
  return column;
};

export const ReasonColumn = ({
  field = "issue_reason_id",
  headerName = "Reason",
  minWidth = "160",
  align = "center",
  headerAlign = "center",
}: ColumnProps): GridColDef => {
  const column: GridColDef = {
    field: field,
    headerName: headerName,
    headerAlign: headerAlign,
    align: align,
    minWidth: parseInt(minWidth),
    renderCell: (params: any) => (
      <ReasonCell
        reasonId={params?.row?.issue_reason_id ?? null}
        classificationId={params?.row?.issue_classification_id ?? null}
      />
    ),
  };
  return column;
};
